import { ExternalLink, MoreHorizontal, Trash2 } from "lucide-react";

import { SortableTableHead, type SortDir } from "@/components/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ProjectSummary } from "@/lib/api";
import { formatDate, formatRelativeTime } from "@/lib/format";
import { getEffectiveStatus } from "@/lib/project-status";
import { cn, safeHttpUrl } from "@/lib/utils";

const STALE_AFTER_DAYS = 60;

function isStale(dateStr: string | null): boolean {
  if (!dateStr) {
    return false;
  }
  const ageMs = Date.now() - new Date(dateStr).getTime();
  return ageMs > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

interface ProjectTableProps {
  projects: ProjectSummary[];
  sortBy: string | null;
  sortDir: SortDir;
  onSort: (column: string) => void;
  onDelete: (project: ProjectSummary) => void;
}

export function ProjectTable({ projects, sortBy, sortDir, onSort, onDelete }: ProjectTableProps) {
  if (projects.length === 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">No projects found.</div>
    );
  }

  const sortProps = { sortBy, sortDir, onSort };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead column="project_name" label="Project" {...sortProps} />
          <SortableTableHead column="owner_name" label="Owner" {...sortProps} />
          <SortableTableHead column="status" label="Status" {...sortProps} />
          <SortableTableHead column="last_commit_at" label="Last updated" {...sortProps} />
          <SortableTableHead column="created_at" label="Created" {...sortProps} />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project) => {
          const status = getEffectiveStatus(project);
          // "Last updated" reflects the repo's last commit (NOR-325); fall back
          // to the DB record timestamp when the GitHub value isn't available.
          const lastUpdated = project.last_commit_at ?? project.updated_at;
          const stale = isStale(lastUpdated);
          const githubUrl = safeHttpUrl(project.github_url);
          return (
            <TableRow
              key={project.id}
              className={cn(stale && "bg-amber-50/50 dark:bg-amber-950/20")}
            >
              <TableCell>
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm font-medium">
                    {project.project_name}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">{project.project_slug}</p>
                </div>
              </TableCell>
              <TableCell>
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm">
                    {project.owner_name || project.owner_email?.split("@")[0] || "Unknown"}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {project.owner_email ?? "—"}
                  </p>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={status.variant}>{status.label}</Badge>
              </TableCell>
              <TableCell
                className={cn(
                  "text-sm",
                  stale
                    ? "font-medium text-amber-700 dark:text-amber-400"
                    : "text-muted-foreground",
                )}
              >
                {formatRelativeTime(lastUpdated)}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDate(project.created_at)}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8">
                      <MoreHorizontal className="size-4" />
                      <span className="sr-only">Project actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {githubUrl && (
                      <DropdownMenuItem asChild>
                        <a href={githubUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Open GitHub
                        </a>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onClick={() => onDelete(project)}>
                      <Trash2 className="mr-2 size-4" /> Delete project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
